/**
 * MyTripsPageNew — full feature parity with old MyTripsPage.
 * Uses useMyTrips hook, share links, swipeable cards, TripCard with rating display.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useMyTrips } from '../hooks/useMyTrips';
import { useDeleteTrip, useCloneTrip } from '../hooks/useTrips';
import { useGenerateShareLink } from '../hooks/useShareLink';
import { PullToRefresh } from '../components/shared/PullToRefresh';
import { SwipeableCard } from '../components/shared/SwipeableCard';
import { EmptyState } from '../components/shared/EmptyState';
import { TripCard } from '../components/TripCard';
import { TripCardSkeleton } from '../components/Skeleton/TripCardSkeleton';
import { Plane, Copy } from 'lucide-react';

export function MyTripsPageNew(): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [copiedTripId, setCopiedTripId] = React.useState<string | null>(null);
  const [cloneTrip, setCloneTrip] = React.useState<any>(null);
  const [cloneName, setCloneName] = React.useState('');
  const [cloneStartDate, setCloneStartDate] = React.useState('');
  const [cloneEndDate, setCloneEndDate] = React.useState('');
  const [deleteConfirmTrip, setDeleteConfirmTrip] = React.useState<any>(null);
  const { data: trips, isLoading } = useMyTrips();
  const deleteTrip = useDeleteTrip();
  const generateShareLink = useGenerateShareLink();
  const cloneTripMutation = useCloneTrip();

  const handleRefresh = React.useCallback(async () => {
    await queryClient.refetchQueries({ queryKey: ['my-trips'] });
  }, [queryClient]);

  const handleShare = async (trip: any) => {
    if (!trip?.id) return;
    try {
      const shareData = await generateShareLink.mutateAsync(trip.id);
      await navigator.clipboard.writeText(shareData.shareUrl);
      setCopiedTripId(trip.id);
      queryClient.invalidateQueries({ queryKey: ['trips'] });
      setTimeout(() => setCopiedTripId(null), 2000);
    } catch { /* silently fail — user can try again */ }
  };

  const openCloneModal = (trip: any) => {
    setCloneTrip(trip);
    setCloneName(trip.name + ' (Copy)');
    setCloneStartDate(trip.startDate);
    setCloneEndDate(trip.endDate);
  };

  const handleCloneConfirm = () => {
    if (!cloneTrip) return;
    cloneTripMutation.mutate(
      {
        tripId: cloneTrip.id,
        data: {
          name: cloneName,
          startDate: cloneStartDate,
          endDate: cloneEndDate,
        },
      },
      {
        onSuccess: (cloned) => {
          setCloneTrip(null);
          navigate(`/trip/${cloned.id}`);
        },
      },
    );
  };

  if (isLoading) {
    return <div className="space-y-3">{[1, 2, 3].map((i) => <TripCardSkeleton key={i} />)}</div>;
  }

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">My Trips</h1>

        {trips?.length === 0 ? (
          <EmptyState icon={Plane} iconColor="text-gray-400" title="No trips yet" subtitle="Plan your next Disney getaway." />
        ) : (
          <ul className="space-y-3">
            {trips?.map((trip: any) => (
              <li key={trip.id}>
                <SwipeableCard onSwipeLeft={() => deleteTrip.mutate(trip.id)}
                  onShare={trip.isOwner ? () => handleShare(trip) : undefined}
                  onClone={trip.isOwner ? () => openCloneModal(trip) : null}>
                  <TripCard trip={trip}
                    onSelect={() => navigate(`/trip/${trip.id}`)}
                    onShare={trip.isOwner ? () => handleShare(trip) : null}
                    onDelete={trip.isOwner ? () => setDeleteConfirmTrip(trip) : null}
                    tripId={trip.id}
                    copiedTripId={copiedTripId}
                    noRing />
                </SwipeableCard>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Delete confirmation modal */}
      {deleteConfirmTrip && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" role="dialog" aria-modal="true">
          <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-surface-dark-card p-6 shadow-2xl mx-4">
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">Delete trip</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
              Delete <span className="font-medium text-gray-700 dark:text-gray-300">{deleteConfirmTrip.name}</span>? This can't be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirmTrip(null)}
                className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-surface-dark-card px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => { deleteTrip.mutate(deleteConfirmTrip.id); setDeleteConfirmTrip(null); }}
                disabled={deleteTrip.isPending}
                className="flex-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clone confirmation modal */}
      {cloneTrip && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" role="dialog" aria-modal="true">
          <div className="w-full max-w-md rounded-2xl bg-white dark:bg-surface-dark-card p-6 shadow-2xl mx-4">
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Clone trip</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Clone <span className="font-medium text-gray-700 dark:text-gray-300">{cloneTrip.name}</span> to a new trip.
            </p>

            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block">Name</label>
                <input
                  type="text"
                  value={cloneName}
                  onChange={(e) => setCloneName(e.target.value)}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-surface-dark-card px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-disney-blue-500 focus:outline-none focus:ring-1 focus:ring-disney-blue-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block">Start date</label>
                  <input
                    type="date"
                    value={cloneStartDate}
                    onChange={(e) => setCloneStartDate(e.target.value)}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-surface-dark-card px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-disney-blue-500 focus:outline-none focus:ring-1 focus:ring-disney-blue-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block">End date</label>
                  <input
                    type="date"
                    value={cloneEndDate}
                    onChange={(e) => setCloneEndDate(e.target.value)}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-surface-dark-card px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-disney-blue-500 focus:outline-none focus:ring-1 focus:ring-disney-blue-500"
                  />
                </div>
              </div>
            </div>

            <div className="mt-5 flex gap-3">
              <button
                onClick={() => setCloneTrip(null)}
                className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-surface-dark-card px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCloneConfirm}
                disabled={cloneTripMutation.isPending}
                className="flex-1 rounded-lg bg-disney-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-disney-blue-600 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                {cloneTripMutation.isPending ? (
                  <>
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Cloning...
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Clone
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </PullToRefresh>
  );
}
